import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {EstadoOnboardingPainel, OnboardingPainelService} from '../onboarding-painel.service';
import {ImpressaoService} from '../../services/impressao.service';
import {EmpresasService} from '../../superadmin/services/empresas.service';
import {ConstantsService} from '../../fidelidade/ConstantsService';
import {EXTENSAO_WHATSAPP_V4} from '../../numero-whatsapp/extensoes-whatsapp';
import {OnbFocoService} from '../apoio/onb-foco.service';

type EtapaImpressora = 'pergunta' | 'nao-tem' | 'instalar' | 'escolher' | 'testando' | 'saiu' | 'falhou';

const QZ_TRAY_DOWNLOAD = 'https://qz.io/download/';
/** Página de teste em texto: 32 colunas cabem em 58 e 80 mm. */
function paginaDeTeste(nomeLoja: string): string {
  const linha = '-'.repeat(32);
  return [
    '', `  ${String(nomeLoja || 'Sua loja').slice(0, 28)}`, '  PAGINA DE TESTE', linha,
    'Pedido #1001 (exemplo)', '1x X-Burger            R$ 24,90', '1x Refrigerante lata   R$  6,00', linha,
    'Total                  R$ 30,90', linha, 'Se voce esta lendo isto, a', 'impressora esta pronta para', 'receber os pedidos.', '', '', ''
  ].join('\n');
}

/**
 * Equipamentos do computador onde o dono recebe os pedidos: extensão do WhatsApp (o cliente recebe cada passo do
 * pedido) e impressora térmica guiada até a página de teste. Nenhum dos dois impede o pedido de teste.
 */
@Component({
  selector: 'app-onb-equipamentos',
  templateUrl: './onb-equipamentos.component.html',
  styleUrls: ['./onb-equipamentos.component.scss']
})
export class OnbEquipamentosComponent implements OnInit, OnDestroy {
  @Input() estado: EstadoOnboardingPainel;
  @Output() mudou = new EventEmitter<EstadoOnboardingPainel>();
  @Output() ajuda = new EventEmitter<{motivo: string; passo: string}>();

  readonly linkExtensao = EXTENSAO_WHATSAPP_V4.url;
  readonly linkQzTray = QZ_TRAY_DOWNLOAD;
  whatsappLoja = '';
  preparandoExtensao = false;
  erroExtensao = '';
  verificandoExtensao = false;

  etapa: EtapaImpressora = 'pergunta';
  impressoras: string[] = [];
  impressoraEscolhida = '';
  papel = '80';
  conectandoQz = false;
  erroImpressora = '';
  private empresa: any = {};
  private verificador: any;
  private inscricao: any;

  constructor(private onboarding: OnboardingPainelService, private impressao: ImpressaoService,
              private empresas: EmpresasService, private constants: ConstantsService, private foco: OnbFocoService) {}

  ngOnInit(): void {
    this.inscricao = this.constants.empresa$.subscribe((empresa: any) => {
      this.empresa = empresa || {};
      this.whatsappLoja = this.formateTelefone(this.empresa.whatsapp);
    });
    const situacao = this.estado?.equipamentos?.impressora;
    if (situacao === 'NAO_TEM') this.etapa = 'nao-tem';
    if (situacao === 'CONFIGURADA') this.etapa = 'saiu';
  }

  ngOnDestroy(): void {
    if (this.verificador) clearInterval(this.verificador);
    if (this.inscricao) this.inscricao.unsubscribe();
  }

  get extensaoConectada(): boolean { return !!this.estado?.equipamentos?.extensaoConectada; }

  private formateTelefone(numero: any): string {
    const d = String(numero || '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return d;
  }

  // ── extensão ────────────────────────────────────────────────────────────

  /**
   * O link da Chrome Web Store abre direto no toque (é um <a>, o navegador não bloqueia). Em paralelo, garante o
   * número da loja como V4 (sem isso o pareamento recusa) e fica olhando se a extensão conectou.
   */
  instaleExtensao(): void {
    this.onboarding.registre('EXTENSAO_ABERTA');
    if (this.preparandoExtensao) return;
    this.preparandoExtensao = true;
    this.erroExtensao = '';
    this.onboarding.prepareExtensao().then(() => {
      this.comeceAVerificar();
    }).catch(e => this.erroExtensao = OnboardingPainelService.mensagem(e, 'Não foi possível preparar a extensão.'))
      .finally(() => this.preparandoExtensao = false);
  }

  private comeceAVerificar(): void {
    if (this.verificador) clearInterval(this.verificador);
    let tentativas = 0;
    this.verificador = setInterval(() => {
      if (++tentativas > 60 || this.extensaoConectada) { clearInterval(this.verificador); return; }
      this.onboarding.estado().then(estado => { if (estado?.equipamentos?.extensaoConectada) this.mudou.emit(estado); }).catch(() => {});
    }, 5000);
  }

  jaInstalei(): void {
    if (this.verificandoExtensao) return;
    this.verificandoExtensao = true;
    this.onboarding.estado().then(estado => {
      if (estado?.equipamentos?.extensaoConectada) return this.mudou.emit(estado);
      return this.onboarding.confirme('extensao').then(novo => this.mudou.emit(novo));
    }).catch(e => this.erroExtensao = OnboardingPainelService.mensagem(e))
      .finally(() => this.verificandoExtensao = false);
  }

  // ── impressora ──────────────────────────────────────────────────────────

  naoTenhoImpressora(): void {
    this.etapa = 'nao-tem';
    this.onboarding.confirme('impressora', {impressora: 'NAO_TEM'}).then(estado => this.mudou.emit(estado)).catch(() => {});
  }

  tenhoImpressora(): void {
    this.erroImpressora = '';
    this.conecteQz();
  }

  mudarResposta(): void {
    this.etapa = 'pergunta';
    this.onboarding.confirme('impressora', {impressora: null}).then(estado => this.mudou.emit(estado)).catch(() => {});
  }

  /** QZ Tray é o programa que deixa o navegador falar com a impressora. Sem ele, ensina a instalar. */
  conecteQz(): void {
    if (this.conectandoQz) return;
    this.conectandoQz = true;
    this.impressao.listeImpressoras().then((r: any) => {
      if (!r?.sucesso) { this.etapa = 'instalar'; return; }
      this.impressoras = (r.data || []).filter((n: string) => !/pdf|xps|onenote|fax|microsoft print/i.test(n));
      this.impressoraEscolhida = this.impressoras.length === 1 ? this.impressoras[0] : '';
      this.etapa = 'escolher';
    }).catch(() => this.etapa = 'instalar')
      .finally(() => this.conectandoQz = false);
  }

  abraQzTray(): void {
    window.location.href = 'qz:launch';
    setTimeout(() => this.conecteQz(), 2500);
  }

  async imprimaTeste(): Promise<void> {
    if (!this.impressoraEscolhida) return this.foco.mostre('onb-impressora', 'Escolha sua impressora térmica nesta lista.');
    this.erroImpressora = '';
    this.etapa = 'testando';
    try {
      await this.salveImpressora();
      await this.impressao.imprimaTexto(paginaDeTeste(this.empresa?.nome), {nome: this.impressoraEscolhida, tamanhoPapel: this.tamanhoPapel});
    } catch (e) {
      this.erroImpressora = String((e as any)?.message || e || 'A impressora não respondeu.');
      this.etapa = 'falhou';
      this.onboarding.registre('IMPRESSORA_FALHOU', this.erroImpressora.slice(0, 180));
    }
  }

  private get tamanhoPapel(): string { return this.papel === '58' ? 'Layout58mm' : 'Layout80mm'; }

  /** Grava a impressora na configuração de impressão de sempre: todo pedido aceito sai no papel. */
  private async salveImpressora(): Promise<void> {
    const config: any = Object.assign({}, this.empresa?.configImpressao || {});
    const automatica = this.constants.possuiRecurso(this.empresa, 'impressao_avancada');
    const outras = (config.impressoras || []).filter((i: any) => !i.removida && i.nome !== this.impressoraEscolhida);
    config.imprimirTXT = true;
    config.imprimirAutomatico = automatica || !!config.imprimirAutomatico;
    config.impressoras = [...outras, {nome: this.impressoraEscolhida, tamanhoPapel: this.tamanhoPapel, layout: 'PedidoCompleto',
      setor: 'Balcão', imprimirAutomatico: automatica}];
    const salvo: any = await this.empresas.salveConfigImpressao({...this.empresa, configImpressao: config});
    this.empresa.configImpressao = salvo || config;
  }

  saiu(): void {
    this.etapa = 'saiu';
    this.onboarding.confirme('impressora', {impressora: 'CONFIGURADA'}).then(estado => this.mudou.emit(estado)).catch(() => {});
  }

  naoSaiu(): void {
    this.etapa = 'falhou';
    this.erroImpressora = 'A página de teste não saiu.';
    this.onboarding.registre('IMPRESSORA_FALHOU', 'página de teste não saiu');
  }

  pecaAjuda(): void {
    this.ajuda.emit({passo: 'impressora', motivo: `${this.erroImpressora || 'A página de teste não saiu'} (impressora ${this.impressoraEscolhida || 'não escolhida'}, papel ${this.papel} mm)`});
  }
}
